package org.chiangmeng.qfast.core.ui.theme

import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import org.chiangmeng.qfast.core.domain.models.AppTheme
import org.jetbrains.compose.resources.Font
import qfast.shared.generated.resources.Res
import qfast.shared.generated.resources.prompt_bold
import qfast.shared.generated.resources.prompt_regular

private val AmberLight = lightColorScheme(
    primary = Color(0xFFFFB300),
    onPrimary = Color.White,
    secondary = Color(0xFFFFCC80),
    surface = Color(0xFFFFFBF7),
    background = Color(0xFFFFFBF7),
    errorContainer = Color(0xFFE5466D),
    onErrorContainer = Color(0xFFFFFFFF)
).copy(surfaceContainerHigh = Color(0xFFFFFFFF))

private val OceanBlueLight = lightColorScheme(
    primary = Color(0xFF0288D1),
    onPrimary = Color.White,
    secondary = Color(0xFF81D4FA),
    surface = Color(0xFFF8FDFF),
    background = Color(0xFFF8FDFF),
    errorContainer = Color(0xFFE5466D),
    onErrorContainer = Color(0xFFFFFFFF)
).copy(surfaceContainerHigh = Color(0xFFFFFFFF))

private val NatureGreenLight = lightColorScheme(
    primary = Color(0xFF2E7D32),
    onPrimary = Color.White,
    secondary = Color(0xFFA5D6A7),
    surface = Color(0xFFF8FFF8),
    background = Color(0xFFF8FFF8),
    errorContainer = Color(0xFFE5466D),
    onErrorContainer = Color(0xFFFFFFFF)
).copy(surfaceContainerHigh = Color(0xFFFFFFFF))

private val RoyalPurpleLight = lightColorScheme(
    primary = Color(0xFF7B1FA2),
    onPrimary = Color.White,
    secondary = Color(0xFFCE93D8),
    surface = Color(0xFFFFF8FF),
    background = Color(0xFFFFF8FF),
    errorContainer = Color(0xFFE5466D),
    onErrorContainer = Color(0xFFFFFFFF)
).copy(surfaceContainerHigh = Color(0xFFFFFFFF))

private val MidnightDark = darkColorScheme(
    primary = Color(0xFF90A4AE),
    onPrimary = Color.Black,
    secondary = Color(0xFF455A64),
    surface = Color(0xFF263238),
    background = Color(0xFF102027)
)

@Composable
fun getAppTypography(): Typography {
    val promptFont = FontFamily(
        Font(Res.font.prompt_regular, FontWeight.Normal),
        Font(Res.font.prompt_regular, FontWeight.Medium),
        Font(Res.font.prompt_bold, FontWeight.Bold),
        Font(Res.font.prompt_bold, FontWeight.ExtraBold),
        Font(Res.font.prompt_bold, FontWeight.Black)
    )
    val defaultTypography = Typography()
    return Typography(
        displayLarge = defaultTypography.displayLarge.copy(fontFamily = promptFont),
        displayMedium = defaultTypography.displayMedium.copy(fontFamily = promptFont),
        displaySmall = defaultTypography.displaySmall.copy(fontFamily = promptFont),
        headlineLarge = defaultTypography.headlineLarge.copy(fontFamily = promptFont),
        headlineMedium = defaultTypography.headlineMedium.copy(fontFamily = promptFont),
        headlineSmall = defaultTypography.headlineSmall.copy(fontFamily = promptFont),
        titleLarge = defaultTypography.titleLarge.copy(fontFamily = promptFont),
        titleMedium = defaultTypography.titleMedium.copy(fontFamily = promptFont),
        titleSmall = defaultTypography.titleSmall.copy(fontFamily = promptFont),
        bodyLarge = defaultTypography.bodyLarge.copy(fontFamily = promptFont),
        bodyMedium = defaultTypography.bodyMedium.copy(fontFamily = promptFont),
        bodySmall = defaultTypography.bodySmall.copy(fontFamily = promptFont),
        labelLarge = defaultTypography.labelLarge.copy(fontFamily = promptFont),
        labelMedium = defaultTypography.labelMedium.copy(fontFamily = promptFont),
        labelSmall = defaultTypography.labelSmall.copy(fontFamily = promptFont)
    )
}

@Composable
fun QFastTheme(
    appTheme: AppTheme = AppTheme.AMBER,
    darkTheme: Boolean = isSystemInDarkTheme(),
    content: @Composable () -> Unit
) {
    val colorScheme = when (appTheme) {
        AppTheme.AMBER -> if (darkTheme) darkColorScheme(primary = Color(0xFFFFCA28), background = Color(0xFF1C1B1F)) else AmberLight
        AppTheme.OCEAN_BLUE -> if (darkTheme) darkColorScheme(primary = Color(0xFF4FC3F7), background = Color(0xFF1C1B1F)) else OceanBlueLight
        AppTheme.NATURE_GREEN -> if (darkTheme) darkColorScheme(primary = Color(0xFF81C784), background = Color(0xFF1C1B1F)) else NatureGreenLight
        AppTheme.ROYAL_PURPLE -> if (darkTheme) darkColorScheme(primary = Color(0xFFBA68C8), background = Color(0xFF1C1B1F)) else RoyalPurpleLight
        AppTheme.MIDNIGHT -> MidnightDark
    }

    MaterialTheme(
        colorScheme = colorScheme,
        typography = getAppTypography()
    ) {
        Surface(
            color = MaterialTheme.colorScheme.background,
            content = content
        )
    }
}